import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type SessionContextService from 'tio-employee/services/session-context';
import type ZendeskService from 'tio-employee/services/zendesk';
import type ForethoughtService from 'tio-employee/services/forethought';
import type RouterService from '@ember/routing/router-service';
import type PartnerService from 'tio-employee/services/partner';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type SessionService from 'tio-employee/services/session';
import type FeaturesService from 'tio-common/services/features';
import type { Store } from '@warp-drive/core';
import type GoogleAnalyticsService from 'tio-employee/services/google-analytics';
import type CableService from 'tio-common/services/cable';
import type TelemetryService from 'tio-common/services/telemetry';
import type ThoughtBubbleRuntimeService from 'tio-common/services/thought-bubble-runtime';
import type ThoughtBubbleBuilderService from 'tio-common/services/thought-bubble-builder';
import type Transition from '@ember/routing/transition';
import ENV from 'tio-employee/config/environment';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import {
  wellnessInfoRequest,
  enqueueWellnessRecalculationRequest,
  getZendeskJwtRequest,
} from 'tio-data/requests/user';

export default class AuthenticatedRoute extends Route {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare zendesk: ZendeskService;
  @service declare forethought: ForethoughtService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare demoMode: DemoModeService;
  @service declare store: Store;
  @service declare googleAnalytics: GoogleAnalyticsService;
  @service declare cable: CableService;
  @service declare telemetry: TelemetryService;
  @service declare thoughtBubbleRuntime: ThoughtBubbleRuntimeService;
  @service declare thoughtBubbleBuilder: ThoughtBubbleBuilderService;

  #widgetMessageListenerInstalled = false;
  #wellnessLoad: Promise<Record<string, unknown> | null> | null = null;

  async beforeModel(transition: Transition) {
    const loggedIn = this.session.requireAuthentication(transition, 'login');

    if (!loggedIn) {
      return;
    }

    // Configure WebSocket connection for ActionCable. In a demo session the
    // cable is hard-DISABLED instead: nothing demo-scripted arrives over it,
    // and no later code path (TioAppearance presence, the approval queue's
    // multi-document subscriptions) may open a live API socket either (same
    // rationale as the Forethought skip below).
    this.cable.disabled = this.demoMode.isActive;
    const token = this.session.data.authenticated?.access_token;
    if (token && !this.cable.isConfigured) {
      this.cable.configure(ENV.apiHost, ENV.apiKey, token, ENV.wsapiHost);
    }

    if (!this.sessionContext.isLoaded) {
      try {
        await this.sessionContext.load();
      } catch (e) {
        console.error('[route:authenticated] Could not load session context', e);
        this.sessionContext.logout();
        return;
      }
    }

    if (!this.sessionContext.user?.id) {
      console.error(
        '[route:authenticated] Missing user id in session context; redirecting to login'
      );
      this.sessionContext.logout();
      return;
    }

    // Started here and awaited in `model()`, so it overlaps the identity, telemetry,
    // and support-chat setup below instead of following it.
    this.#wellnessLoad = this.loadWellness(this.sessionContext.user.id);

    try {
      this.zendesk.hideAllChatWidgets();
      this.zendesk.setWidgetLocale();
    } catch (e) {
      console.error('[route:authenticated] Could not set zendesk user', e);
    }

    void this.seedForethought(token);

    try {
      const currentRole = this.sessionContext.currentRole;
      this.telemetry.setUser({
        id: this.sessionContext.user.id,
        person_id: this.sessionContext.user.person?.id ?? undefined,
        role: currentRole?.role,
        relationship_type: currentRole?.relationshipType,
      });

      const company = this.sessionContext.currentCompany;
      this.telemetry.setContext({
        company_id: company?.id ?? undefined,
        company_setting_id: company?.companySetting?.id ?? undefined,
      });
    } catch (e) {
      console.error('Error setting up telemetry user', e);
    }

    // Initialize thought bubble runtime for auto-triggering flows on route change
    // Outside env gate — runtime is safe on all environments (only triggers flows that exist in the API)
    try {
      this.thoughtBubbleRuntime.initialize({
        company: this.sessionContext.user?.employees?.[0]?.company,
        employee: this.sessionContext.user?.employees?.[0],
        roles: this.sessionContext.currentRole ? [this.sessionContext.currentRole] : [],
      });
    } catch (e) {
      console.error('Error initializing thought bubble runtime', e);
    }

    // Check if this page was opened as a visual builder target
    // Outside env gate because it's self-gating via ?tb-builder= query param
    try {
      this.thoughtBubbleBuilder.checkActivation();
    } catch (e) {
      console.error('Error checking thought bubble builder activation', e);
    }

    // @ts-expect-error: transition.intent may have url (URL transition) or name (named route transition)
    const isRootUrlTransition = transition.intent?.url === '/';
    const targetRouteName = transition.to?.name;
    const isRootNamedTransition =
      targetRouteName === 'authenticated.index' || targetRouteName === 'authenticated';

    if (isRootUrlTransition || isRootNamedTransition) {
      this.router.transitionTo(this.sessionContext.landingRoute);
    }
  }

  async model() {
    // `/me` already materialized this user with its boot includes, so the record is
    // read from the session context rather than fetched a second time.
    const user = this.sessionContext.user as unknown as UserModel;
    const userId = user?.id;
    if (!userId) {
      console.error('[route:authenticated] Missing user id in model hook; redirecting to login');
      this.router.transitionTo('login');
      return { user, wellnessData: null };
    }

    const wellnessData = await (this.#wellnessLoad ?? this.loadWellness(userId));
    this.#wellnessLoad = null;

    return { user, wellnessData };
  }

  /**
   * Fetches the wellness-train profile for the signed-in user, enqueueing a background
   * recalculation when none exists yet.
   *
   * @param userId id of the signed-in user.
   * @returns the raw user-profile payload, or `null` in a demo session or when the fetch fails.
   */
  private async loadWellness(userId: string): Promise<Record<string, unknown> | null> {
    // In a demo session the fabricated /me user id is a non-live sentinel, so skip
    // the wellness fetch AND the fire-and-forget recalculation POST — they have no
    // demo matcher and would hit the real API as /users/<sentinel>/... (404 spam,
    // and an unwanted side-effecting POST under the real token).
    if (this.demoMode.isActive) {
      return null;
    }

    let wellnessData: Record<string, unknown> | null = null;
    try {
      const response = await this.store.request(wellnessInfoRequest(userId));
      wellnessData = response.content as Record<string, unknown>;
    } catch (e) {
      console.error('[route:authenticated] Failed to load wellness train:', e);
    }

    // If no wellness data exists, trigger a background recalculation (fire-and-forget).
    // Data will be available on the next page load.
    if (!wellnessData) {
      this.store.request(enqueueWellnessRecalculationRequest(userId)).catch(() => undefined);
    }

    return wellnessData;
  }

  /**
   * Seeds the Forethought support-chat widget once the Zendesk JWT arrives. Never awaited
   * by the route: the widget is not part of the page, so its round trip must not delay
   * the first render.
   *
   * @param token access token forwarded to the widget alongside the Zendesk JWT.
   */
  private async seedForethought(token: string | undefined) {
    // Skip the Forethought/Zendesk support-chat seeding in a demo session: it
    // is seeded with the REAL access token + Zendesk JWT, so a fabricated demo
    // identity must not open a real support session under a fake name.
    if (
      !this.partner.isZendeskEnabled ||
      !this.partner.isForethoughtChatEnabled ||
      this.demoMode.isActive
    ) {
      return;
    }

    const { user } = this.sessionContext;
    try {
      const zendeskJwtResp = await this.store.request(getZendeskJwtRequest());
      const zendeskJwt = zendeskJwtResp.content as { token: string };
      // A logout or a newer login while the JWT was in flight leaves this token stale;
      // seeding then would hand the widget the previous identity.
      if (this.session.data.authenticated?.access_token !== token) {
        return;
      }

      this.forethought.seed({
        firstName: user.person?.firstName,
        lastName: user.person?.lastName,
        fullName: user.person?.fullName,
        zendeskJwt: zendeskJwt.token,
        tioUserJwt: token,
        tioApiKey: ENV.apiKey,
        email: user.employees?.[0]?.email,
        partnerName: user.employees?.[0]?.company.name,
        onReady: () => this.zendesk.hideForethoughtWidget(),
      });
      // Singleton route; install once — beforeModel re-runs on re-login.
      if (!this.#widgetMessageListenerInstalled) {
        this.#widgetMessageListenerInstalled = true;
        window.addEventListener('message', (e) => {
          if (e.data.event === 'forethoughtWidgetIntegrationHandoff') {
            this.zendesk.zendeskAction('messenger', 'resetWidget');
          }
          // Re-hide widget when user closes chat so only sidebar Support can reopen it
          if (e.data.event === 'forethoughtWidgetClosed') {
            this.zendesk.hideForethoughtWidget();
          }
        });
      }
    } catch (e) {
      console.error('[route:authenticated] Could not setup Forethought chat', e);
    }
  }

  afterModel() {
    // Initialize Google Analytics tracking now that user is fully authenticated
    // and currentRole is available
    this.googleAnalytics.initializeTracking();

    // Track this route transition with user context
    const currentRole = this.sessionContext.currentRole;
    this.googleAnalytics.trackRouteTransition('authenticated', {
      user_authenticated: true,
      role_type: currentRole?.relationshipType,
      company_id: this.sessionContext.currentCompany?.id,
    });

    // Note: Wellness train data is now loaded in model() hook
  }
}
